import { supabase } from './supabaseClient';

export interface SelfAssessmentConfig {
  id: string;
  facilityId: string;
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'biannual' | 'annual';
  allowAnytime: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export class SelfAssessmentConfigService {
  /**
   * Get self-assessment configuration for a facility.
   * Returns null if no config exists (feature never configured).
   */
  static async getConfig(facilityId: string): Promise<SelfAssessmentConfig | null> {
    const { data, error } = await supabase
      .from('self_assessment_config')
      .select('*')
      .eq('facility_id', facilityId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching self-assessment config:', error);
      throw error;
    }

    if (!data) return null;

    return this.mapDbToConfig(data);
  }

  /**
   * Create or update self-assessment configuration for a facility.
   */
  static async saveConfig(
    facilityId: string,
    config: Pick<SelfAssessmentConfig, 'enabled' | 'frequency' | 'allowAnytime'>
  ): Promise<SelfAssessmentConfig> {
    const { data, error } = await supabase
      .from('self_assessment_config')
      .upsert(
        {
          facility_id: facilityId,
          enabled: config.enabled,
          frequency: config.frequency,
          allow_anytime: config.allowAnytime,
          updated_at: new Date().toISOString()
        },
        { onConflict: 'facility_id' }
      )
      .select()
      .single();

    if (error) {
      console.error('Error saving self-assessment config:', error);
      throw error;
    }

    return this.mapDbToConfig(data);
  }

  private static mapDbToConfig(row: any): SelfAssessmentConfig {
    return {
      id: row.id,
      facilityId: row.facility_id,
      enabled: row.enabled ?? false,
      frequency: row.frequency || 'weekly',
      allowAnytime: row.allow_anytime ?? false,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }
}
